import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import { Fp1Contract, CONTRACT_DISCOUNT_TYPE_LABELS } from '../contracts.types';
import FiyrPodLogo from 'logos/FiyrPod_Logo.png';

const GREEN = '#B4E12C';
const BLACK = '#000000';
const DARK = '#1a1a1a';
const GRAY = '#666666';
const LIGHT_GRAY = '#f5f5f5';
const BORDER = '#000000';

const s = StyleSheet.create({
    page: {
        fontFamily: 'Helvetica',
        fontSize: 9,
        color: DARK,
        paddingTop: 44,
        paddingBottom: 56,
        paddingHorizontal: 50,
        lineHeight: 1.5,
    },
    // ── Header ──
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `2 solid ${GREEN}`,
        paddingBottom: 10,
        marginBottom: 16,
    },
    logo: {
        width: 72,
        height: 'auto',
    },
    headerRight: {
        alignItems: 'flex-end',
    },
    headerDocType: {
        fontSize: 13,
        fontFamily: 'Helvetica-Bold',
        color: BLACK,
        letterSpacing: 1,
        textTransform: 'uppercase',
    },
    headerSub: {
        fontSize: 8,
        color: GRAY,
        marginTop: 2,
    },
    // ── Title bar ──
    titleBar: {
        backgroundColor: GREEN,
        paddingVertical: 9,
        paddingHorizontal: 12,
        marginBottom: 14,
    },
    titleText: {
        fontSize: 13,
        fontFamily: 'Helvetica-Bold',
        color: BLACK,
    },
    titleStatus: {
        fontSize: 7.5,
        color: BLACK,
        marginTop: 3,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
    },
    // ── Parties ──
    partiesRow: {
        flexDirection: 'row',
        gap: 10,
        marginBottom: 14,
    },
    partyBox: {
        flex: 1,
        border: `1 solid ${BORDER}`,
        padding: 9,
        backgroundColor: LIGHT_GRAY,
    },
    partyLabel: {
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        color: BLACK,
        borderBottom: `1 solid ${GREEN}`,
        paddingBottom: 3,
        marginBottom: 5,
    },
    partyName: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        color: BLACK,
    },
    partyDetail: {
        fontSize: 8,
        color: GRAY,
        marginTop: 2,
    },
    // ── Section ──
    section: {
        marginBottom: 12,
    },
    sectionHeader: {
        backgroundColor: GREEN,
        paddingVertical: 4,
        paddingHorizontal: 8,
        marginBottom: 0,
    },
    sectionTitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        color: BLACK,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
    },
    sectionBody: {
        border: `1 solid ${BORDER}`,
        borderTop: 'none',
    },
    row: {
        flexDirection: 'row',
        borderBottom: `1 solid #e0e0e0`,
    },
    rowLabel: {
        width: '40%',
        fontSize: 8.5,
        color: GRAY,
        paddingVertical: 4,
        paddingHorizontal: 8,
        backgroundColor: LIGHT_GRAY,
        borderRight: `1 solid #e0e0e0`,
    },
    rowValue: {
        flex: 1,
        fontSize: 8.5,
        fontFamily: 'Helvetica-Bold',
        color: BLACK,
        paddingVertical: 4,
        paddingHorizontal: 8,
    },
    // ── Body text ──
    bodyText: {
        fontSize: 8.5,
        color: '#333',
        lineHeight: 1.6,
        marginBottom: 6,
    },
    numberedItem: {
        flexDirection: 'row',
        marginBottom: 5,
        paddingLeft: 4,
    },
    numberedBullet: {
        width: 20,
        fontSize: 8.5,
        color: BLACK,
        fontFamily: 'Helvetica-Bold',
    },
    numberedText: {
        flex: 1,
        fontSize: 8.5,
        color: '#333',
        lineHeight: 1.6,
    },
    // ── Custom terms ──
    customTermsBox: {
        border: `1 solid ${BORDER}`,
        padding: 10,
        backgroundColor: '#fffff0',
        marginTop: 0,
    },
    // ── Signature block ──
    signaturesSection: {
        marginTop: 18,
        paddingTop: 12,
        borderTop: `2 solid ${GREEN}`,
    },
    sigTitle: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        color: BLACK,
        marginBottom: 12,
    },
    sigRow: {
        flexDirection: 'row',
        gap: 20,
    },
    sigBlock: {
        flex: 1,
    },
    sigPartyHeader: {
        backgroundColor: GREEN,
        paddingVertical: 4,
        paddingHorizontal: 8,
        marginBottom: 10,
    },
    sigPartyLabel: {
        fontSize: 7.5,
        fontFamily: 'Helvetica-Bold',
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        color: BLACK,
    },
    sigFieldLabel: {
        fontSize: 7.5,
        color: GRAY,
        marginBottom: 2,
    },
    sigLine: {
        height: 22,
        borderBottom: `1 solid ${BLACK}`,
        marginBottom: 8,
        justifyContent: 'flex-end',
    },
    sigSignedName: {
        fontSize: 14,
        fontFamily: 'Helvetica-Oblique',
        color: BLACK,
        paddingBottom: 2,
    },
    sigFilledValue: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        color: BLACK,
        paddingBottom: 2,
    },
    // ── Footer ──
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 50,
        right: 50,
        borderTop: `1 solid ${GREEN}`,
        paddingTop: 5,
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    footerText: {
        fontSize: 7,
        color: GRAY,
    },
    pageNumber: {
        fontSize: 7,
        color: GRAY,
    },
});

function fmtDate(d: string | null) {
    if (!d) return 'N/A';
    // Append time to prevent UTC→local timezone shift on date-only strings
    return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

function fmtToday() {
    return new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

const PageHeader = ({ subtitle }: { subtitle: string }) => (
    <View style={s.header} fixed>
        <Image style={s.logo} src={FiyrPodLogo} />
        <View style={s.headerRight}>
            <Text style={s.headerDocType}>{subtitle}</Text>
            <Text style={s.headerSub}>Fiyrpod · 1TUL · Athlete Management Technology</Text>
        </View>
    </View>
);

const PageFooter = ({ label }: { label: string }) => (
    <View style={s.footer} fixed>
        <Text style={s.footerText}>{label} · Confidential</Text>
        <Text
            style={s.pageNumber}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
        />
    </View>
);

interface DataRowProps {
    label: string;
    value: string | number | null | undefined;
}
const DataRow = ({ label, value }: DataRowProps) => (
    <View style={s.row}>
        <Text style={s.rowLabel}>{label}</Text>
        <Text style={s.rowValue}>{value != null ? String(value) : 'N/A'}</Text>
    </View>
);

interface Props {
    contract: Fp1Contract;
}

export default function Fp1ContractPdf({ contract }: Props) {
    const discountLabel = contract.discount_type
        ? CONTRACT_DISCOUNT_TYPE_LABELS[contract.discount_type]
        : 'None';

    const discountValueDisplay = contract.discount_value
        ? contract.discount_type === 'percentage'
            ? `${contract.discount_value}%`
            : `$${contract.discount_value}`
        : 'N/A';

    const contractRef = `${contract.uuid.slice(0, 8).toUpperCase()}`;

    return (
        <Document
            title={contract.contract_name}
            author="Fiyrpod / 1TUL"
            subject="Service Agreement"
        >
            {/* ── Page 1: Contract Terms ── */}
            <Page size="LETTER" style={s.page}>
                <PageHeader subtitle="SERVICE AGREEMENT" />

                {/* Title bar */}
                <View style={s.titleBar}>
                    <Text style={s.titleText}>{contract.contract_name}</Text>
                    <Text style={s.titleStatus}>
                        Ref: {contractRef} · Status: {contract.status.replace('_', ' ')} ·
                        Generated: {fmtToday()}
                    </Text>
                </View>

                {/* Parties */}
                <View style={s.partiesRow}>
                    <View style={s.partyBox}>
                        <Text style={s.partyLabel}>Service Provider</Text>
                        <Text style={s.partyName}>Fiyrpod / 1TUL</Text>
                        <Text style={s.partyDetail}>Athlete Management Technology</Text>
                        <Text style={s.partyDetail}>fiyrpod.com · 1tul.com</Text>
                    </View>
                    <View style={s.partyBox}>
                        <Text style={s.partyLabel}>Client / Organization</Text>
                        <Text style={s.partyName}>{contract.organization_name ?? 'TBD'}</Text>
                        <Text style={s.partyDetail}>
                            Org ID: {contract.organization_uuid ?? 'N/A'}
                        </Text>
                    </View>
                </View>

                {/* Contract Details */}
                <View style={s.section}>
                    <View style={s.sectionHeader}>
                        <Text style={s.sectionTitle}>Contract Details</Text>
                    </View>
                    <View style={s.sectionBody}>
                        <DataRow label="Effective Date" value={fmtDate(contract.start_date)} />
                        <DataRow
                            label="Expiration Date"
                            value={fmtDate(contract.expiration_date)}
                        />
                        {contract.renewal_reminder_date && (
                            <DataRow
                                label="Renewal Reminder"
                                value={fmtDate(contract.renewal_reminder_date)}
                            />
                        )}
                        <DataRow label="Auto Renewal" value={contract.auto_renew ? 'Yes' : 'No'} />
                        <DataRow
                            label="Prepared By"
                            value={contract.created_by_name ?? 'Fiyrpod Admin'}
                        />
                    </View>
                </View>

                {/* Pricing Terms */}
                <View style={s.section}>
                    <View style={s.sectionHeader}>
                        <Text style={s.sectionTitle}>Pricing &amp; Financial Terms</Text>
                    </View>
                    <View style={s.sectionBody}>
                        <DataRow label="Discount / Pricing Type" value={discountLabel} />
                        {contract.discount_value && (
                            <DataRow label="Discount Value" value={discountValueDisplay} />
                        )}
                    </View>
                </View>

                {/* Operational Terms */}
                <View style={s.section}>
                    <View style={s.sectionHeader}>
                        <Text style={s.sectionTitle}>Operational Terms</Text>
                    </View>
                    <View style={s.sectionBody}>
                        <DataRow label="Maximum Devices" value={contract.max_devices} />
                    </View>
                </View>

                {/* Custom Terms */}
                {contract.custom_terms && (
                    <View style={s.section}>
                        <View style={s.sectionHeader}>
                            <Text style={s.sectionTitle}>Custom / Addendum Terms</Text>
                        </View>
                        <View style={s.customTermsBox}>
                            <Text style={s.bodyText}>{contract.custom_terms}</Text>
                        </View>
                    </View>
                )}

                <PageFooter label={`${contract.contract_name} — Service Agreement`} />
            </Page>

            {/* ── Page 2: Exhibit A — Device T&C ── */}
            <Page size="LETTER" style={s.page}>
                <PageHeader subtitle="EXHIBIT A — DEVICE TERMS" />

                <View style={s.section}>
                    <View style={s.sectionHeader}>
                        <Text style={s.sectionTitle}>Fiyrpod Device Terms &amp; Conditions</Text>
                    </View>
                    <View style={[s.sectionBody, { padding: 10 }]}>
                        <Text style={s.bodyText}>
                            {`These Device Terms and Conditions ("Device Terms") govern Client's use of all Fiyrpod hardware devices ("Pods") provided by Fiyrpod / 1TUL ("Provider") under this Agreement. By accepting delivery of any Pod, Client agrees to be bound by these Device Terms.`}
                        </Text>

                        {(
                            [
                                [
                                    '1.',
                                    "DEVICE OWNERSHIP & LICENSE. All Pods remain the sole property of Provider unless explicitly stated otherwise in writing. Client receives a limited, non-transferable license to use the Pods solely for Client's internal athletic training and performance monitoring purposes during the Term of this Agreement.",
                                ],
                                [
                                    '2.',
                                    'DEVICE CARE & RESPONSIBILITY. Client is responsible for the reasonable care, custody, and control of all Pods in its possession. Client shall store Pods in appropriate conditions (temperature 32°F–104°F, dry environment) and shall not expose Pods to excessive moisture, impact, or other conditions reasonably expected to cause damage.',
                                ],
                                [
                                    '3.',
                                    "DAMAGE & REPLACEMENT. Accidental damage and normal wear-and-tear replacements may be covered at Provider's discretion. Replacements required due to gross negligence, misuse, or theft shall be billed to Client at Provider's then-current replacement pricing.",
                                ],
                                [
                                    '4.',
                                    'DEVICE LIMITS. Client shall not use more Pods simultaneously than the maximum device count specified in this Agreement. Additional devices may be provisioned subject to a written addendum and additional fees.',
                                ],
                                [
                                    '5.',
                                    "RETURN OF DEVICES. Upon termination or expiration of this Agreement, Client shall return all Pods to Provider within fourteen (14) calendar days in good working condition, reasonable wear excepted. Unreturned or damaged-beyond-normal-wear Pods will be charged to Client at Provider's replacement cost.",
                                ],
                                [
                                    '6.',
                                    'MODIFICATIONS. Client shall not modify, reverse-engineer, disassemble, or attempt to repair any Pod. All firmware and software embedded in the Pods are proprietary to Provider and are licensed, not sold.',
                                ],
                                [
                                    '7.',
                                    'WARRANTY DISCLAIMER. PODS ARE PROVIDED "AS IS" WITH RESPECT TO THIRD-PARTY COMPONENTS. PROVIDER WARRANTS THAT PODS WILL FUNCTION MATERIALLY AS DESCRIBED IN PROVIDER\'S PUBLISHED DOCUMENTATION UNDER NORMAL USE. ALL OTHER WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE, ARE DISCLAIMED.',
                                ],
                            ] as [string, string][]
                        ).map(([num, text]) => (
                            <View key={num} style={s.numberedItem}>
                                <Text style={s.numberedBullet}>{num}</Text>
                                <Text style={s.numberedText}>{text}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                <PageFooter label={`${contract.contract_name} — Exhibit A`} />
            </Page>

            {/* ── Page 3: Exhibit B — Platform T&C ── */}
            <Page size="LETTER" style={s.page}>
                <PageHeader subtitle="EXHIBIT B — SERVICE TERMS" />

                <View style={s.section}>
                    <View style={s.sectionHeader}>
                        <Text style={s.sectionTitle}>1TUL Platform Terms &amp; Conditions</Text>
                    </View>
                    <View style={[s.sectionBody, { padding: 10 }]}>
                        <Text style={s.bodyText}>
                            {`These Service Terms govern Client's access to and use of the 1TUL software platform, mobile applications, web interfaces, APIs, and related services (collectively, the "Platform") provided by 1TUL ("Provider").`}
                        </Text>

                        {(
                            [
                                [
                                    '1.',
                                    "PLATFORM LICENSE. Provider grants Client a limited, non-exclusive, non-transferable, revocable license to access and use the Platform during the Term solely for Client's internal athletic performance management, program design, athlete tracking, and related coaching operations.",
                                ],
                                [
                                    '2.',
                                    "ATHLETE DATA. Client owns all athlete performance data entered into or generated through the Platform by Client or Client's athletes. Provider shall process such data solely to provide the Platform services and as described in Provider's Privacy Policy. Client represents that it has obtained all necessary consents from athletes whose data is entered into the Platform.",
                                ],
                                [
                                    '3.',
                                    'SUBSCRIPTION & PAYMENT. Platform access fees are due in accordance with the pricing terms specified in this Agreement. Invoices unpaid for more than thirty (30) days may result in suspension of Platform access. Disputed amounts must be raised in writing within thirty (30) days of invoice date.',
                                ],
                                [
                                    '4.',
                                    "CONFIDENTIALITY. Each party agrees to keep the other's Confidential Information (defined as non-public business, technical, and financial information disclosed under this Agreement) confidential using at least the same degree of care it uses to protect its own confidential information, but not less than reasonable care.",
                                ],
                                [
                                    '5.',
                                    "INTELLECTUAL PROPERTY. Provider retains all rights, title, and interest in the Platform, Pods, and all related intellectual property. No license to Provider's intellectual property is granted except as expressly set forth herein. Client retains all rights to Client's pre-existing intellectual property and athlete data.",
                                ],
                                [
                                    '6.',
                                    "LIMITATION OF LIABILITY. IN NO EVENT SHALL EITHER PARTY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES, INCLUDING LOST PROFITS, ARISING OUT OF OR RELATED TO THIS AGREEMENT, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. PROVIDER'S TOTAL CUMULATIVE LIABILITY ARISING OUT OF OR RELATED TO THIS AGREEMENT SHALL NOT EXCEED THE FEES PAID BY CLIENT IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.",
                                ],
                                [
                                    '7.',
                                    "INDEMNIFICATION. Each party shall indemnify, defend, and hold harmless the other party from third-party claims arising from: (a) the indemnifying party's breach of this Agreement; or (b) the indemnifying party's gross negligence or willful misconduct.",
                                ],
                                [
                                    '8.',
                                    "TERMINATION. Either party may terminate this Agreement for material breach upon thirty (30) days written notice if the breach is not cured within the notice period. Provider may terminate immediately upon Client's insolvency, bankruptcy, or non-payment. Upon termination, Client shall return all Pods and cease using the Platform.",
                                ],
                                [
                                    '9.',
                                    'GOVERNING LAW & DISPUTES. This Agreement shall be governed by the laws of the State where Provider is incorporated, without regard to conflicts of law principles. The parties agree to attempt to resolve disputes through good-faith negotiation before initiating formal proceedings.',
                                ],
                                [
                                    '10.',
                                    'ENTIRE AGREEMENT. This Agreement, including all Exhibits, constitutes the entire agreement between the parties regarding its subject matter and supersedes all prior agreements. Amendments must be in writing and signed by authorized representatives of both parties.',
                                ],
                            ] as [string, string][]
                        ).map(([num, text]) => (
                            <View key={num} style={s.numberedItem}>
                                <Text style={s.numberedBullet}>{num}</Text>
                                <Text style={s.numberedText}>{text}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                <PageFooter label={`${contract.contract_name} — Exhibit B`} />
            </Page>

            {/* ── Page 4: Signature Page ── */}
            <Page size="LETTER" style={s.page}>
                <PageHeader subtitle="SIGNATURE PAGE" />

                <View style={[s.section, { marginBottom: 18 }]}>
                    <Text style={s.bodyText}>
                        IN WITNESS WHEREOF, the parties hereto have executed this Agreement as of
                        the date last signed below. Each party represents and warrants that the
                        person signing below is duly authorized to execute this Agreement on behalf
                        of such party and to bind such party to the terms herein.
                    </Text>
                    <Text style={s.bodyText}>
                        This Agreement incorporates by reference: the Contract Details set forth
                        above, Exhibit A (Fiyrpod Device Terms &amp; Conditions), and Exhibit B
                        (1TUL Platform Terms &amp; Conditions), together with any custom addendum
                        terms specified herein.
                    </Text>
                </View>

                <View style={s.signaturesSection}>
                    <View style={s.sectionHeader}>
                        <Text style={s.sectionTitle}>Authorized Signatures</Text>
                    </View>

                    <View style={[s.sigRow, { marginTop: 14 }]}>
                        {/* Provider side — auto-signed */}
                        <View style={s.sigBlock}>
                            <View style={s.sigPartyHeader}>
                                <Text style={s.sigPartyLabel}>
                                    Service Provider — Fiyrpod / 1TUL
                                </Text>
                            </View>

                            <Text style={s.sigFieldLabel}>Signature</Text>
                            <View style={s.sigLine}>
                                <Text style={s.sigSignedName}>
                                    {contract.created_by_name ?? 'Fiyrpod Admin'}
                                </Text>
                            </View>

                            <Text style={s.sigFieldLabel}>Printed Name</Text>
                            <View style={s.sigLine}>
                                <Text style={s.sigFilledValue}>
                                    {contract.created_by_name ?? 'Fiyrpod Admin'}
                                </Text>
                            </View>

                            <Text style={s.sigFieldLabel}>Title / Role</Text>
                            <View style={s.sigLine}>
                                <Text style={s.sigFilledValue}>
                                    {contract.created_by_name === 'Nick Priest'
                                        ? 'CEO'
                                        : 'Authorized Representative'}
                                </Text>
                            </View>

                            <Text style={s.sigFieldLabel}>Date</Text>
                            <View style={s.sigLine}>
                                <Text style={s.sigFilledValue}>{fmtToday()}</Text>
                            </View>
                        </View>

                        {/* Client side — blank for manual signature */}
                        <View style={s.sigBlock}>
                            <View style={s.sigPartyHeader}>
                                <Text style={s.sigPartyLabel}>
                                    Client —{' '}
                                    {(contract.organization_name ?? 'Organization').toUpperCase()}
                                </Text>
                            </View>

                            <Text style={s.sigFieldLabel}>Signature</Text>
                            <View style={s.sigLine} />

                            <Text style={s.sigFieldLabel}>Printed Name</Text>
                            <View style={s.sigLine} />

                            <Text style={s.sigFieldLabel}>Title / Role</Text>
                            <View style={s.sigLine} />

                            <Text style={s.sigFieldLabel}>Date</Text>
                            <View style={s.sigLine} />
                        </View>
                    </View>
                </View>

                <PageFooter label={`${contract.contract_name} — Signature Page`} />
            </Page>
        </Document>
    );
}
